import React from 'react';
import { Box, Grid, Typography, SvgIcon } from '@mui/material';
import * as yup from 'yup';
import {
  SparkForm,
  SparkFormButton,
  SparkFormResetButton,
  SparkTextField,
  SparkFormImageUploader,
  SparkDropdown,
  SparkFormPhoneInput,
} from 'sharedComponents/SparkForm';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
// Replacing TwitterIcon with a custom X icon
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { useUpdateMarketplaceAccount } from 'hooks/Marketplace/useUpdateMarketplaceAccount';
import { MarketplaceAccount } from 'types/MarketplaceAccount';
import { useCountries } from 'hooks/Countries/useCountries';

// Custom X icon component
const XIcon = (props: React.ComponentProps<typeof SvgIcon>) => (
  <SvgIcon {...props} viewBox='0 0 24 24'>
    <path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
  </SvgIcon>
);

type ProfessionalInfoFormType = {
  description: string;
  website: string;
  linkedin: string;
  youtube: string;
  twitter: string;
  facebook: string;
  imageURL: string;
  displayName: string;
  country: string;
  email: string;
  phone: string;
};

const initialValues: ProfessionalInfoFormType = {
  description: '',
  website: '',
  linkedin: '',
  twitter: '',
  youtube: '',
  facebook: '',
  imageURL: '',
  displayName: '',
  country: '',
  email: '',
  phone: '',
};

const validationSchema = yup.object({
  description: yup
    .string()
    .required('Description is required')
    .max(600, 'Description cannot exceed 600 characters'),
  website: yup.string().url('Please enter a valid URL'),
  linkedin: yup.string().url('Please enter a valid URL'),
  youtube: yup.string().url('Please enter a valid URL'),
  twitter: yup.string().url('Please enter a valid URL'),
  facebook: yup.string().url('Please enter a valid URL'),
  imageURL: yup.string(),
  displayName: yup
    .string()
    .required('Display name is required')
    .max(50, 'Display name cannot exceed 50 characters'),
  country: yup.string().required('Country is required'),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  phone: yup.string(),
});

type ProfessionalInfoFormProps = {
  marketplaceAccount: MarketplaceAccount | undefined;
};

function ProfessionalInfoForm({
  marketplaceAccount,
}: ProfessionalInfoFormProps) {
  const { mutateAsync: updateMarketplaceAccount, isLoading } =
    useUpdateMarketplaceAccount();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const {
    dropdownOptions: countries,
    countryCodeDropdownOptions: countriesOptions,
  } = useCountries();

  const formValues = React.useMemo(() => {
    if (!marketplaceAccount) return initialValues;

    const attributes = marketplaceAccount.attributes;

    return {
      description: attributes.description || '',
      website: attributes.website || '',
      linkedin: (attributes as any).linkedIn || '',
      twitter: (attributes as any).x || '',
      youtube: (attributes as any).youtube || '',
      facebook: (attributes as any).facebook || '',
      imageURL: attributes.imageURL || '',
      displayName: (attributes as any).displayName || '',
      country: (attributes as any).countryID
        ? String((attributes as any).countryID)
        : '',
      email: (attributes as any).email || '',
      phone: (attributes as any).phone || '',
    };
  }, [marketplaceAccount]);

  const handleSubmit = async (values: ProfessionalInfoFormType) => {
    if (!marketplaceAccount) return;

    setIsSubmitting(true);
    try {
      const payload = {
        id: marketplaceAccount.id,
        description: values.description.trim() || null,
        website: values.website.trim() || null,
        linkedIn: values.linkedin.trim() || null,
        x: values.twitter.trim() || null,
        youtube: values.youtube.trim() || null,
        facebook: values.facebook.trim() || null,
        imageURL: values.imageURL || null,
        displayName: values.displayName.trim() || null,
        countryID: values.country ? Number(values.country) : null,
        email: values.email.trim() || null,
        phone: values.phone.trim() || null,
      };

      await updateMarketplaceAccount(payload);
    } catch (error) {
      console.error('Error updating professional info:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const linkFieldStyle = {
    marginBottom: '4px',
  };

  const onImageUpload = async (imageURL: string) => {
    await updateMarketplaceAccount({
      id: marketplaceAccount?.id || '',
      imageURL,
    });
  };

  return (
    <Box
      width='100%'
      bgcolor='white'
      borderRadius='20px'
      border={(theme) => `1px solid ${theme.palette.borderColor}`}
    >
      <SparkForm<ProfessionalInfoFormType>
        initialValues={formValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSave={handleSubmit}
        containerProps={{ spacing: 1, padding: 2 }}
      >
        <Grid container justifyContent='center' sx={{ mb: 2, pt: 2 }}>
          <Grid item>
            <SparkFormImageUploader
              name='imageURL'
              aspectRatio={1}
              onUpload={onImageUpload}
              containerName='public-assets'
              shouldDisplayImageAfterUpload={true}
            />
          </Grid>
        </Grid>

        <SparkTextField
          name='displayName'
          label='Display Name'
          placeholder='Enter your public display name'
          inputProps={{ maxLength: 50 }}
          showCounter={true}
          required={true}
          size={6}
          containerStyle={{ marginBottom: '8px' }}
        />

        <SparkDropdown
          name='country'
          label='Country'
          placeholder='Select your country'
          required={true}
          containerStyle={{ marginBottom: '16px' }}
          size={6}
        >
          {countries?.map((country) => country)}
        </SparkDropdown>

        <SparkTextField
          name='description'
          label='Description'
          placeholder="Share a bit about your work experience, cool projects you've completed, and your areas of expertise."
          multiline
          rows={5}
          inputProps={{ maxLength: 600 }}
          showCounter
          required={true}
          size={12}
          containerStyle={{ marginBottom: '16px' }}
        />

        <SparkTextField
          name='email'
          label='Email'
          placeholder='Enter your email address'
          size={6}
          required={true}
        />

        <SparkFormPhoneInput
          name='phone'
          label='Phone'
          placeholder='Enter your phone number'
          countriesOptions={countriesOptions}
          size={6}
        />

        <Grid item xs={12} sx={{ mt: 3, mb: 0.5, pl: 2 }}>
          <Typography variant='subtitle1'>Links</Typography>
        </Grid>

        <SparkTextField
          name='website'
          label=' '
          placeholder='Enter your website URL'
          startAdornment={<LanguageIcon sx={{ mr: 1 }} color='primary' />}
          size={6}
          containerStyle={linkFieldStyle}
        />

        <SparkTextField
          name='twitter'
          label=' '
          placeholder='Enter your X profile URL'
          startAdornment={<XIcon sx={{ mr: 1 }} color='primary' />}
          size={6}
          containerStyle={linkFieldStyle}
        />

        <SparkTextField
          name='facebook'
          label=' '
          placeholder='Enter your Facebook profile URL'
          startAdornment={<FacebookIcon sx={{ mr: 1 }} color='primary' />}
          size={6}
          containerStyle={linkFieldStyle}
        />

        <SparkTextField
          name='linkedin'
          label=' '
          placeholder='Enter your LinkedIn profile URL'
          startAdornment={<LinkedInIcon sx={{ mr: 1 }} color='primary' />}
          size={6}
          containerStyle={linkFieldStyle}
        />

        <SparkTextField
          name='youtube'
          label=' '
          placeholder='Enter your YouTube channel URL'
          startAdornment={<YouTubeIcon sx={{ mr: 1 }} color='primary' />}
          size={6}
          containerStyle={linkFieldStyle}
        />

        <Grid
          item
          container
          xs={12}
          gap={2}
          justifyContent='flex-end'
          flexWrap='nowrap'
          sx={{ mt: 2 }}
        >
          <SparkFormResetButton
            variant='outlined'
            sx={{ width: '200px', padding: '10px 20px', marginTop: '10px' }}
            disabled={isSubmitting}
          >
            Cancel
          </SparkFormResetButton>
          <SparkFormButton
            sx={{ width: '200px', padding: '10px 20px', marginTop: '10px' }}
            disabled={isSubmitting}
            shouldRequireFieldUpdates={true}
          >
            Save
          </SparkFormButton>
        </Grid>
      </SparkForm>
    </Box>
  );
}

export default ProfessionalInfoForm;
